import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {makeApiCall} from '../../../../Api/makeApiCall';
import LoadingSpinner from '../../../Components/LoadingSpinner';
import {makeApolloClientCall} from '../../../../Api/makeApolloApiCall';
import ArchiveFilenamesList from './ArchiveFilenamesList';
import ItemCodeOutliers from './ItemCodeOutliers';
import {findArchiveFiles, listArchiveFtpFolders} from './findArchiveFiles';
import _ from 'lodash';

export const storeWeekItemDateOutliersQuery = `
query storeWeekItemDataOutliers($retailerId: Int!, $storeIds: [Int!] $weekEndDate: String!){
records:storeWeekItemDataOutliers(retailerId:$retailerId, storeIds: $storeIds, weekEndDate: $weekEndDate, percent: 25)
    {
        salesAmount
        spinsItemCode
        itemCode
        weekEndDate
        spinsStoreId
        spinsRetailerId
        percentTotalDollar
        itemDescription
    }
}`

export const archiveFilenamesQuery = `
query archiveFilenames($retailerId: Int!, $storeId: Int!, $date: String!, $source: salesSourceType!,
                       $includePlu: Boolean!, $includeIfps: Boolean, $productSnapshotOnly: Boolean!, $includeFilledWeeksOnly: Boolean) {
  salesIngestionSummary(retailerId: $retailerId, storeIds: [$storeId], startDate: $date, endDate: $date,
                        sources: [$source], includePlu: $includePlu, includeIfps: $includeIfps,
                        productSnapshotOnly: $productSnapshotOnly, includeFilledWeeksOnly: $includeFilledWeeksOnly) {
    stores { weekEndData { date archiveFilenames } }
  }
}`

const ArchiveFilenameModalContent = ({retailerId, storeId, weekEndDate, source, salesFilters, skipUseEffect}) => {
    const [archiveFileList, updateArchiveFileList] = useState(undefined);
    const [itemCodeOutliers, updateItemCodeOutliers] = useState(undefined);
    const [archiveFilesError, setArchiveFilesError] = useState(false);
    const [itemCodeOutliersError, setItemCodeOutliersError] = useState(false);
    const [parentRetailerId, updateRetailerId] = useState(retailerId);
    const {includePlu, includeIfps, productSnapshotOnly, includeFillOnly} = salesFilters;

    React.useEffect(() => {
        if (skipUseEffect) return;
        makeApolloClientCall(storeWeekItemDateOutliersQuery, false, {
            retailerId,
            storeIds: [storeId],
            weekEndDate
        }).then(results => updateItemCodeOutliers(results.records))
            .catch(() => setItemCodeOutliersError(true));

        const archiveFilenamesRequest = makeApolloClientCall(archiveFilenamesQuery, false, {
            retailerId,
            storeId,
            date: weekEndDate,
            source,
            includePlu,
            includeIfps,
            productSnapshotOnly,
            includeFilledWeeksOnly: source === 'cleansed' && includeFillOnly
        }).then(results => results.salesIngestionSummary.stores
            .flatMap(store => store.weekEndData)
            .find(weekEndData => weekEndData.date === weekEndDate)?.archiveFilenames || []);

        const supplementalStores = encodeURIComponent(`gs://spins-edp-${process.env.REACT_APP_ENV}-services/stores/supplementalStores.json`);
        const archiveRetailerIdRequest = makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/getGcsFileByPath?path=${supplementalStores}`)
            .then((result) => _.findLast(result.data, x => x.retailerIdDisplayed === retailerId && x.storeId === storeId)?.retailerId ?? retailerId);

        const archiveFoldersRequest = archiveRetailerIdRequest.then(listArchiveFtpFolders);

        Promise.all([archiveRetailerIdRequest, archiveFilenamesRequest, archiveFoldersRequest])
            .then(([archiveRetailerId, archiveFilenames, archiveFolders]) => {
                updateRetailerId(archiveRetailerId);
                return findArchiveFiles(archiveRetailerId, archiveFilenames, archiveFolders);
            })
            .then(fileList => updateArchiveFileList(fileList))
            .catch(() => setArchiveFilesError(true));
    }, [retailerId, storeId, weekEndDate, source, includePlu, includeIfps, productSnapshotOnly, includeFillOnly, skipUseEffect]);

    const renderSection = (content, error, errorTestId, errorText) => {
        if (error) {
            return <div className={'error-message'} data-testid={errorTestId}>{errorText}</div>;
        }
        return content || <LoadingSpinner spinnerClassName={'spinner-container-medium'}/>;
    };

    return <>
        {renderSection(archiveFileList && <ArchiveFilenamesList archiveFileList={archiveFileList} retailerId={parentRetailerId}/>,
            archiveFilesError, 'archive-files-error', 'Error loading archive files')}
        {renderSection(itemCodeOutliers && <ItemCodeOutliers outliers={itemCodeOutliers}/>,
            itemCodeOutliersError, 'item-code-outliers-error', 'Error loading item code outliers')}
    </>;
};

ArchiveFilenameModalContent.propTypes = {
    retailerId: PropTypes.number,
    storeId: PropTypes.number,
    weekEndDate: PropTypes.string,
    source: PropTypes.string,
    salesFilters: PropTypes.shape({
        includePlu: PropTypes.bool,
        includeIfps: PropTypes.bool,
        productSnapshotOnly: PropTypes.bool,
        includeFillOnly: PropTypes.bool
    }).isRequired,
    skipUseEffect: PropTypes.bool
};

export default ArchiveFilenameModalContent
